import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Store, Mail, Lock, Eye, EyeOff,
  ShieldCheck, PackageOpen, Users, BarChart3
} from 'lucide-react';

export const Route = createFileRoute('/login')({
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate({ to: '/' });
    } catch (err: any) {
      const code = err?.code || '';
      if (code === 'auth/user-not-found') setError('No account found with this email');
      else if (code === 'auth/wrong-password') setError('Incorrect password');
      else if (code === 'auth/invalid-credential') setError('Invalid email or password');
      else if (code === 'auth/invalid-email') setError('Please enter a valid email address');
      else setError(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-gradient-to-br from-blue-50 via-white to-blue-100 font-sans">
      {/* Left — Professional Branding Panel */}
      <div className="hidden lg:flex flex-1 flex-col justify-between py-12 px-16 relative overflow-hidden bg-gradient-to-br from-blue-600 to-blue-800">
        <div className="absolute inset-0 opacity-[0.05]"
          style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20 20.5V18H0v-2h20v-2H0v-2h20v-2H0V8h20V6H0V4h20V2H0V0h22v20h2V0h2v20h2V0h2v20h2V0h2v20h2V0h2v20h2v2H20v-1.5zM0 20h2v20H0V20zm4 0h2v20H4V20zm4 0h2v20H8V20zm4 0h2v20h-2V20zm4 0h2v20h-2V20zm4 4h20v2H20v-2zm0 4h20v2H20v-2zm0 4h20v2H20v-2zm0 4h20v2H20v-2z' fill='%23ffffff' fill-opacity='1' fill-rule='evenodd'/%3E%3C/svg%3E")` }}
        />
        <div className="absolute top-20 right-0 w-96 h-96 bg-blue-400/20 rounded-full blur-3xl mix-blend-screen" />
        <div className="absolute bottom-10 left-10 w-72 h-72 bg-blue-300/15 rounded-full blur-3xl mix-blend-screen" />

        <div className="relative z-10 w-full max-w-lg">
          <div className="flex items-center gap-3 mb-16">
            <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center shadow-lg shadow-blue-900/20">
              <Store className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-white text-xl font-bold tracking-tight">HardwarePOS</h2>
              <p className="text-blue-200/70 text-[10px] uppercase tracking-wider font-semibold">Cloud Edition</p>
            </div>
          </div>

          <h1 className="text-white text-4xl font-extrabold leading-tight mb-4 tracking-tight">
            Professional Grade <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-white">
              Retail Management
            </span>
          </h1>

          <p className="text-blue-100/70 text-[15px] leading-relaxed mb-12 max-w-md">
            The complete accounting, inventory, and point of sale solution built explicitly for the modern hardware enterprise.
          </p>

          <div className="space-y-6">
            {[
              { icon: PackageOpen, title: 'Multi-Location Inventory', desc: 'Track stock levels, reorder points, and inter-branch transfers.' },
              { icon: ShieldCheck, title: 'FBR Digital Invoicing', desc: 'Direct IRIS sandbox & production integration with QR codes.' },
              { icon: Users, title: 'B2B & Retail CRM', desc: 'Manage wholesale limits, receivable aging, and walk-in sales.' },
              { icon: BarChart3, title: 'Financial Analytics', desc: 'Real-time profit & loss, cash flow, and tax liability reporting.' },
            ].map((Feature, i) => (
              <div key={i} className="flex gap-4 items-start group">
                <div className="w-10 h-10 rounded-lg bg-white/10 backdrop-blur-sm border border-white/10 flex items-center justify-center flex-shrink-0 group-hover:bg-white/20 transition-colors">
                  <Feature.icon className="w-5 h-5 text-blue-200" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-[13px] mb-1">{Feature.title}</h3>
                  <p className="text-blue-200/50 text-[11px] leading-relaxed">{Feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 text-blue-200/40 text-xs">
          © {new Date().getFullYear()} HardwarePOS Enterprise. All rights reserved.
        </div>
      </div>

      {/* Right — Login Form */}
      <div className="w-full lg:w-[480px] bg-gradient-to-b from-blue-50 to-white flex flex-col justify-center relative shadow-[-20px_0_40px_-20px_rgba(59,130,246,0.1)] z-20">
        <div className="w-full max-w-[360px] mx-auto px-6">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-lg">
              <Store className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-slate-900 text-xl font-bold">HardwarePOS</h2>
              <p className="text-slate-500 text-xs uppercase tracking-wider font-semibold">Cloud Edition</p>
            </div>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-1.5 tracking-tight">Welcome Back</h2>
            <p className="text-slate-500 text-[13px]">Sign in to access your administrative dashboard.</p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 mb-6 flex items-start gap-2.5">
              <div className="mt-0.5"><ShieldCheck className="w-4 h-4 text-red-500" /></div>
              <p className="text-red-700 text-[12px] font-medium leading-snug">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wide mb-1.5">Email Address</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="w-4 h-4 text-slate-400" />
                </div>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                  placeholder="admin@hardwarepos.com"
                  className="w-full pl-9 pr-4 py-2 bg-white rounded-lg border border-slate-200 text-slate-900 text-[13px] placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                />
              </div>
            </div>

            <div className="mb-2">
              <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wide mb-1.5">Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="w-4 h-4 text-slate-400" />
                </div>
                <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6}
                  placeholder="••••••••"
                  className="w-full pl-9 pr-10 py-2 bg-white rounded-lg border border-slate-200 text-slate-900 text-[13px] placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 transition-colors">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button type="submit" disabled={loading}
              className="w-full mt-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-[13px] font-bold transition-all disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2 group shadow-sm shadow-blue-600/20">
              {loading ? (
                <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Authenticating</>
              ) : (
                <><ShieldCheck className="w-4 h-4" /> Secure Sign In</>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
